import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Menu, X, Phone, Globe, User, LogOut, Settings, ChevronDown, Building2, Users, TrendingUp, Briefcase, Handshake } from 'lucide-react';
import NotificationBell from './NotificationBell';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';

const GOVERNO_ANGOLA_LOGO = 'https://customer-assets.emergentagent.com/job_56b9fc4d-0615-46a9-bb44-4b743949a554/artifacts/kwinu6m7_Governo%20de%20Angola%20Logo.png';
const MIREX_LOGO = 'https://customer-assets.emergentagent.com/job_56b9fc4d-0615-46a9-bb44-4b743949a554/artifacts/iv1nhi8r_Logo_Mirex_color_2.png';

function DropdownLink({ to, icon: Icon, title, description, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-start gap-3 p-3 rounded-md hover:bg-gray-50 transition-colors group"
      data-testid={`nav-link-${to.replace(/\//g, '-').slice(1)}`}
    >
      <div className="w-9 h-9 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#CC0000] group-hover:text-white transition-colors">
        <Icon size={16} className="text-[#CC0000] group-hover:text-white transition-colors" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900 group-hover:text-[#CC0000] transition-colors">{title}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </Link>
  );
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const { user, logout, isPublisher } = useAuth();
  const { lang, changeLang, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;
  const isActiveGroup = (paths) => paths.some(p => location.pathname.startsWith(p));

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const closeMobile = () => {
    setMenuOpen(false);
    setMobileDropdown(null);
  };

  const toggleMobileDropdown = (key) => {
    setMobileDropdown(prev => prev === key ? null : key);
  };

  return (
    <header className="sticky top-0 z-50 w-full shadow-sm">
      {/* Top Bar */}
      <div className="bg-[#CC0000] text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-9">
          <div className="flex items-center gap-2">
            <Phone size={13} />
            <span className="font-medium">{t('nav.emergency')}: +34 914 35 64 30</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border-l border-red-400 pl-3">
              <Globe size={13} />
              {['pt', 'es', 'en'].map(l => (
                <button key={l} onClick={() => changeLang(l)} data-testid={`lang-${l}`}
                  className={`px-1.5 py-0.5 rounded text-xs font-semibold transition-all ${lang === l ? 'bg-white text-red-700' : 'hover:bg-red-500 text-white'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo" onClick={closeMobile}>
              <img src={GOVERNO_ANGOLA_LOGO} alt="Governo de Angola" className="h-12 w-auto" />
              <div className="flex flex-col items-start">
                <img src={MIREX_LOGO} alt="MIREX" className="h-8 w-auto" />
                <p className="text-xs font-semibold text-gray-700 mt-0.5 hidden sm:block">Embaixada de Angola no Reino de Espanha</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden lg:flex" data-testid="desktop-nav">
              <NavigationMenuList className="gap-0">
                {/* Inicio */}
                <NavigationMenuItem>
                  <Link to="/" data-testid="nav-inicio"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center ${isActive('/') ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:text-[#CC0000] hover:bg-gray-50'}`}>
                    {t('nav.home')}
                  </Link>
                </NavigationMenuItem>

                {/* A Embaixada - Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    data-testid="nav-embaixada-dropdown"
                    className={`px-3 py-2 text-sm font-medium transition-colors ${isActiveGroup(['/embaixada', '/corpo-diplomatico', '/sobre']) ? 'text-[#CC0000]' : 'text-gray-700 hover:text-[#CC0000]'} bg-transparent hover:bg-gray-50 data-[state=open]:bg-gray-50`}
                  >
                    {t('nav.embassy')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[340px] p-3" data-testid="embaixada-dropdown-content">
                      <DropdownLink to="/embaixada" icon={Building2} title={t('nav.embassy')} description={t('nav.embassy_desc')} />
                      <DropdownLink to="/corpo-diplomatico" icon={Users} title={t('nav.diplomatic_corps')} description={t('nav.diplomatic_corps_desc')} />
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* O Consulado */}
                <NavigationMenuItem>
                  <Link to="/servicos-consulares" data-testid="nav-consulado"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center ${isActive('/servicos-consulares') ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:text-[#CC0000] hover:bg-gray-50'}`}>
                    {t('nav.consulate')}
                  </Link>
                </NavigationMenuItem>

                {/* Investimento - Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    data-testid="nav-investimento-dropdown"
                    className={`px-3 py-2 text-sm font-medium transition-colors ${isActiveGroup(['/investir-em-angola', '/oportunidades', '/relacoes-bilaterais']) ? 'text-[#CC0000]' : 'text-gray-700 hover:text-[#CC0000]'} bg-transparent hover:bg-gray-50 data-[state=open]:bg-gray-50`}
                  >
                    {t('nav.investment')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[380px] p-3" data-testid="investimento-dropdown-content">
                      <DropdownLink to="/investir-em-angola" icon={TrendingUp} title={t('nav.invest_angola')} description={t('nav.invest_angola_desc')} />
                      <DropdownLink to="/oportunidades" icon={Briefcase} title={t('nav.business_opportunities')} description={t('nav.business_opportunities_desc')} />
                      <DropdownLink to="/relacoes-bilaterais" icon={Handshake} title={t('nav.bilateral_relations')} description={t('nav.bilateral_relations_desc')} />
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Noticias */}
                <NavigationMenuItem>
                  <Link to="/noticias" data-testid="nav-noticias"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center ${isActiveGroup(['/noticias', '/eventos']) ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:text-[#CC0000] hover:bg-gray-50'}`}>
                    {t('nav.news')}
                  </Link>
                </NavigationMenuItem>

                {/* Contactos */}
                <NavigationMenuItem>
                  <Link to="/contactos" data-testid="nav-contactos"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center ${isActive('/contactos') ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:text-[#CC0000] hover:bg-gray-50'}`}>
                    {t('nav.contact')}
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Auth & Mobile */}
            <div className="flex items-center gap-2">
              {user ? (
                <div className="hidden lg:flex items-center gap-2">
                  <NotificationBell />
                  {isPublisher() && (
                    <Link to="/admin/dashboard" data-testid="backoffice-link"
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-700 transition-colors">
                      <Settings size={14} />
                      {t('nav.backoffice')}
                    </Link>
                  )}
                  {user.role === 'user' && (
                    <Link to="/Users" data-testid="profile-link"
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#CC0000] text-white rounded-md text-sm font-medium hover:bg-[#A30000] transition-colors">
                      <User size={14} />
                      Minha Conta
                    </Link>
                  )}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-200">
                    <User size={14} className="text-gray-500" />
                    <span className="text-sm text-gray-700">{user.full_name?.split(' ')[0]}</span>
                    <button onClick={handleLogout} data-testid="logout-btn"
                      className="ml-1 text-gray-400 hover:text-red-600 transition-colors">
                      <LogOut size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="hidden lg:flex gap-2">
                  <Link to="/login" data-testid="login-btn"
                    className="px-4 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    {t('nav.login')}
                  </Link>
                  <Link to="/registar" data-testid="register-btn"
                    className="px-4 py-1.5 text-sm font-medium text-white bg-[#CC0000] rounded-md hover:bg-[#A30000] transition-colors">
                    {t('nav.register')}
                  </Link>
                </div>
              )}
              <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100" data-testid="mobile-menu-btn">
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <Link to="/" onClick={closeMobile}
              className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive('/') ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
              {t('nav.home')}
            </Link>

            {/* A Embaixada */}
            <div>
              <button onClick={() => toggleMobileDropdown('embassy')}
                className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${isActiveGroup(['/embaixada', '/corpo-diplomatico']) ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
                {t('nav.embassy')}
                <ChevronDown size={16} className={`transition-transform ${mobileDropdown === 'embassy' ? 'rotate-180' : ''}`} />
              </button>
              {mobileDropdown === 'embassy' && (
                <div className="ml-4 space-y-1 mt-1">
                  <Link to="/embaixada" onClick={closeMobile} className="block px-3 py-2 text-sm text-gray-600 hover:text-[#CC0000] hover:bg-gray-50 rounded-md">{t('nav.embassy')}</Link>
                  <Link to="/corpo-diplomatico" onClick={closeMobile} className="block px-3 py-2 text-sm text-gray-600 hover:text-[#CC0000] hover:bg-gray-50 rounded-md">{t('nav.diplomatic_corps')}</Link>
                </div>
              )}
            </div>

            {/* O Consulado */}
            <Link to="/servicos-consulares" onClick={closeMobile}
              className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive('/servicos-consulares') ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
              {t('nav.consulate')}
            </Link>

            {/* Investimento */}
            <div>
              <button onClick={() => toggleMobileDropdown('investment')}
                className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${isActiveGroup(['/investir-em-angola', '/oportunidades', '/relacoes-bilaterais']) ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
                {t('nav.investment')}
                <ChevronDown size={16} className={`transition-transform ${mobileDropdown === 'investment' ? 'rotate-180' : ''}`} />
              </button>
              {mobileDropdown === 'investment' && (
                <div className="ml-4 space-y-1 mt-1">
                  <Link to="/investir-em-angola" onClick={closeMobile} className="block px-3 py-2 text-sm text-gray-600 hover:text-[#CC0000] hover:bg-gray-50 rounded-md">{t('nav.invest_angola')}</Link>
                  <Link to="/oportunidades" onClick={closeMobile} className="block px-3 py-2 text-sm text-gray-600 hover:text-[#CC0000] hover:bg-gray-50 rounded-md">{t('nav.business_opportunities')}</Link>
                  <Link to="/relacoes-bilaterais" onClick={closeMobile} className="block px-3 py-2 text-sm text-gray-600 hover:text-[#CC0000] hover:bg-gray-50 rounded-md">{t('nav.bilateral_relations')}</Link>
                </div>
              )}
            </div>

            {/* Noticias */}
            <Link to="/noticias" onClick={closeMobile}
              className={`block px-3 py-2 rounded-md text-sm font-medium ${isActiveGroup(['/noticias']) ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
              {t('nav.news')}
            </Link>

            {/* Contactos */}
            <Link to="/contactos" onClick={closeMobile}
              className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive('/contactos') ? 'text-[#CC0000] bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
              {t('nav.contact')}
            </Link>

            {/* Auth */}
            <div className="pt-3 border-t border-gray-200">
              {user ? (
                <div className="space-y-1">
                  {isPublisher() && (
                    <Link to="/admin/dashboard" onClick={closeMobile}
                      className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md">
                      {t('nav.backoffice')}
                    </Link>
                  )}
                  <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md">
                    {t('nav.logout')}
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" onClick={closeMobile} className="flex-1 text-center px-3 py-2 text-sm font-medium border border-gray-300 rounded-md">
                    {t('nav.login')}
                  </Link>
                  <Link to="/registar" onClick={closeMobile} className="flex-1 text-center px-3 py-2 text-sm font-medium text-white bg-[#CC0000] rounded-md">
                    {t('nav.register')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
